import { Plane, Hotel, CarFront, Camera, ShieldCheck, Headset } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";

const CONCIERGE_IMG = "https://images.pexels.com/photos/5490756/pexels-photo-5490756.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const INCLUSION_IMGS = {
  flights: "https://images.unsplash.com/photo-1597388522516-a755e0fcfc72?crop=entropy&cs=srgb&fm=jpg&q=85",
  hotels: "https://images.unsplash.com/photo-1661954864180-e61dea14208a?crop=entropy&cs=srgb&fm=jpg&q=85",
  transfers: "https://images.unsplash.com/photo-1597388522438-8f99f4cf354b?crop=entropy&cs=srgb&fm=jpg&q=85",
  sightseeing: "https://images.pexels.com/photos/12203602/pexels-photo-12203602.jpeg?auto=compress&cs=tinysrgb&w=940",
};

const CARDS = [
  { key: "flights", icon: Plane, title: "Flights, sorted", text: "Trusted airlines, sensible timings and generous baggage — booked and reconfirmed for you." },
  { key: "hotels", icon: Hotel, title: "Stays we would choose", text: "Every hotel is personally vetted for location, service and that holiday feeling." },
  { key: "transfers", icon: CarFront, title: "Private, always", text: "No shared shuttles or waiting around. Your driver, your vehicle, your schedule." },
  { key: "sightseeing", icon: Camera, title: "Sightseeing that matters", text: "Iconic sights plus the quiet corners, with local guides who love their cities." },
];

export default function WhyUs() {
  return (
    <section id="why-us" className="scroll-mt-24 py-24 sm:py-32" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Kicker>The T&amp;T Promise</Kicker>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-50 tracking-tight max-w-2xl">
            One booking. <em className="gold-text not-italic">Every detail</em> handled.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-5">
          {CARDS.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.08} className="md:col-span-3 lg:col-span-3">
              <div className="group relative h-64 rounded-2xl overflow-hidden border border-white/10 hover:border-gold/40 transition-colors duration-500">
                <img src={INCLUSION_IMGS[c.key]} alt={c.title} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx-900 via-onyx-900/50 to-onyx-900/10" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <c.icon size={22} className="text-gold" />
                  <h3 className="mt-3 font-serif text-xl text-slate-50">{c.title}</h3>
                  <p className="mt-1 text-sm text-slate-300 max-w-md">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.15} className="md:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-gold/20 bg-onyx-800">
              <div className="grid lg:grid-cols-2">
                <div className="p-8 sm:p-12 flex flex-col justify-center">
                  <Kicker>Concierge Care</Kicker>
                  <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-slate-50">A human, whenever you need one</h3>
                  <p className="mt-4 text-slate-300 text-sm sm:text-base leading-relaxed max-w-md">
                    From visa paperwork to a midnight flight change, your dedicated travel designer
                    stays one call away — before, during and after the trip.
                  </p>
                  <div className="mt-8 grid sm:grid-cols-2 gap-5">
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="text-gold shrink-0" size={20} />
                      <p className="text-sm text-slate-300">Transparent pricing with zero hidden fees, in writing.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Headset className="text-gold shrink-0" size={20} />
                      <p className="text-sm text-slate-300">24×7 on-trip support across every timezone you visit.</p>
                    </div>
                  </div>
                </div>
                <div className="relative h-64 lg:h-auto">
                  <img src={CONCIERGE_IMG} alt="Luxury resort poolside at dusk" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-onyx-800 via-onyx-800/20 to-transparent" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
